import { useMemo } from 'react';
import styled from '@emotion/styled';
import MuiTooltip from '@mui/material/Tooltip';
import { BaseMessageButton } from '@trinity/chat-components/components/Chat/Message.Button';

export type MarkdownBadgeProps = {
  className?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  text: React.ReactNode;
  children?: React.ReactNode;
  maxWidth?: string;
  inline?: boolean;
};

type Props = MarkdownBadgeProps;

export const MarkdownBadge = (props: Props) => {
  const StyledBaseMessageButton = useMemo(
    () =>
      styled(BaseMessageButton)(({ theme }) => ({
        display: props.inline ? 'inline-block' : 'block',
        minWidth: '0',
        height: 'auto',
        minHeight: 30,
        cursor: 'auto',
        borderRadius: '6px',
        margin: '2px',

        // Default: allow wrapping for short-text badges
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflow: 'visible',

        // When maxWidth is set: single line with ellipsis truncation — no overflow
        ...(props.maxWidth && {
          whiteSpace: 'nowrap',
          wordBreak: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: props.maxWidth,
        }),

        // Disable hover effects
        '&:hover': {
          color: theme.palette.badge.citation.color,
          backgroundColor: theme.palette.badge.citation.bg,
          borderColor: theme.palette.badge.citation.bg,
        },
      })),
    [props.maxWidth, props.inline]
  );

  return (
    <MuiTooltip {...props} title={props.text} placement={props.placement} arrow={false}>
      <StyledBaseMessageButton className={props.className}>{props.children}</StyledBaseMessageButton>
    </MuiTooltip>
  );
};
